<script setup lang="ts">
import {onMounted, onUnmounted, Ref, ref, reactive, computed } from 'vue';
import backendCall from "../../services/AxiosService";
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import { useSocketStore } from '@/stores/socketStore';

const props = defineProps({
  maxTrains: {
    type: Number,
    default: 5
  }
});

// Types for the train data
interface Train {
  train_row_id: number;
  train_origin_departure_date: string;
  eta: string | null;
  train_id: string;
  algorithm_eta_interval?: string;
  algorithm_eta?: string;
  eta_history?: unknown[];
}

interface EnhancedTrain extends Train {
  origin_depart_date_string: string;
  eta_string: string | null;
  location: string;
  timeData?: {
    totalTime?: number;
    waitTime?: number;
    trackProtectionTime?: number;
  } | null;
  staffNotesCount?: number;
  dashed_train_id?: string;
  gensetCount?: number;
  electricCount?: number;
  requestCount?: number;
  dieselCount?: number;
  etaHistory?: EtaHistoryEntry[];
}

interface ApiResponse {
  content: Train[];
  data?: {
    station?: string;
    description?: string;
    provstate?: string;
    time?: string;
    etaIntervalString?: string;
    etaTime?: string;
  };
}

interface EtaHistoryEntry {
  eta: string | null;
  dataSource: string;
  entererName?: string | null;
  entererId?: number | null;
  createdAt?: string | null;
}

const trains: Ref<EnhancedTrain[]> = ref([]);
let fetchTrainsInterval: ReturnType<typeof setInterval>;
const locationsLoading = ref(false);
const socketStore = useSocketStore();

// Define emits
const emit = defineEmits(['data-loaded']);

// Computed property to limit the number of trains displayed
const displayedTrains = computed(() => {
  if (props.maxTrains && trains.value.length > 0) {
    return trains.value.slice(0, props.maxTrains);
  }
  return trains.value;
});

// Store location data for each train (including RMS ETAs)
const trainLocations = reactive<Record<number, any>>({});

const normalizeEtaHistoryEntry = (entry: any): EtaHistoryEntry => {
  const rawSource = entry?.data_source ?? entry?.dataSource ?? entry?.source ?? 'manual';
  const dataSource = typeof rawSource === 'string' && rawSource.trim().length > 0 ? rawSource.trim() : 'manual';

  const rawEta = entry?.eta ?? null;
  const etaIso = rawEta ? new Date(rawEta).toISOString() : null;

  const rawCreatedAt = entry?.created_at ?? entry?.createdAt ?? null;
  const createdAtIso = rawCreatedAt ? new Date(rawCreatedAt).toISOString() : null;

  return {
    eta: etaIso,
    dataSource,
    entererName: entry?.enterer_name ?? entry?.entererName ?? null,
    entererId: entry?.enterer_id ?? entry?.entererId ?? null,
    createdAt: createdAtIso
  };
};

const etaSourceMeta = (source?: string | null) => {
  const normalized = (source || 'manual').toLowerCase();
  const metaMap: Record<string, { label: string; icon: string; iconClass: string; textClass: string }> = {
    request: {
      label: 'Request',
      icon: 'pi pi-clipboard',
      iconClass: 'text-purple-500 dark:text-purple-400',
      textClass: 'text-purple-700 dark:text-purple-300'
    },
    manual: {
      label: 'Manual',
      icon: 'pi pi-user-edit',
      iconClass: 'text-slate-500 dark:text-slate-300',
      textClass: 'text-slate-600 dark:text-slate-300'
    },
    train_add_domestics: {
      label: 'Train Setup',
      icon: 'pi pi-wrench',
      iconClass: 'text-emerald-500 dark:text-emerald-400',
      textClass: 'text-emerald-600 dark:text-emerald-300'
    },
    train_add_electrics: {
      label: 'Train Setup',
      icon: 'pi pi-wrench',
      iconClass: 'text-emerald-500 dark:text-emerald-400',
      textClass: 'text-emerald-600 dark:text-emerald-300'
    },
    domestic_generator: {
      label: 'Domestic Generator',
      icon: 'pi pi-cog',
      iconClass: 'text-amber-500 dark:text-amber-400',
      textClass: 'text-amber-600 dark:text-amber-300'
    }
  };

  const meta = metaMap[normalized];
  if (meta) {
    return meta;
  }

  const label = normalized
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ') || 'ETA';

  return {
    label,
    icon: 'pi pi-clock',
    iconClass: 'text-blue-500 dark:text-blue-400',
    textClass: 'text-blue-600 dark:text-blue-300'
  };
};

const formatEtaDisplay = (isoString: string | null) => {
  if (!isoString) {
    return 'No ETA';
  }

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return 'Invalid';
  }

  return date.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    month: 'short',
    day: 'numeric',
    hourCycle: 'h23'
  });
};

const formatEtaTooltip = (entry: EtaHistoryEntry) => {
  const meta = etaSourceMeta(entry.dataSource);
  const parts: string[] = [meta.label];

  if (entry.entererName) {
    parts.push(`by ${entry.entererName}`);
  }

  if (entry.createdAt) {
    const createdAtDate = new Date(entry.createdAt);
    if (!Number.isNaN(createdAtDate.getTime())) {
      parts.push(createdAtDate.toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hourCycle: 'h23'
      }));
    }
  }

  return parts.join(' • ');
};

// Fetch location data for a single train (including RMS ETAs)
const fetchTrainLocation = async (trainId: number) => {
  try {
    const response = await backendCall.get(`trains/locationbyid?trainid=${trainId}`);
    if (response?.data?.data) {
      trainLocations[trainId] = response.data.data;
    } else {
      // Store an empty object when no location data is available
      trainLocations[trainId] = {
        station: null,
        description: null,
        provstate: null,
        time: null
      };
    }
  } catch (error) {
    console.error(`Error fetching location for train ${trainId}:`, error);
    trainLocations[trainId] = {
      station: null,
      description: null,
      provstate: null,
      time: null
    };
  }
};

// Fetch location data for all trains
const fetchAllTrainLocations = async () => {
  try {
    const locationPromises = trains.value.map(train => 
      fetchTrainLocation(train.train_row_id)
    );
    await Promise.all(locationPromises);
  } catch (error) {
    console.error('Error fetching train locations:', error);
  }
};

const fetchTrains = async () => {
  try {
    locationsLoading.value = true;
    const response = await backendCall.get<ApiResponse>('trains/lineup');
    const data = response.data;

    if (Array.isArray(data.content)) {
      // Process the train data with a new array to ensure Vue reactivity
      const processedTrains = data.content.map(train => {
        const etaHistory = Array.isArray(train.eta_history)
          ? (train.eta_history as unknown[]).map(entry => normalizeEtaHistoryEntry(entry))
          : [];

        return {
          ...train,
          train_row_id: Number(train.train_row_id), // Convert to number
          origin_depart_date_string: new Date(train.train_origin_departure_date).toDateString(),
          eta_string: train.eta ? new Date(train.eta).toLocaleString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hourCycle: 'h23'
          }) : null,
          location: '', // Will be populated by fetchAllTrainLocations
          etaHistory
        };
      });

      // Set to a completely new array to force reactivity
      trains.value = processedTrains;

      // Fetch additional data for all trains
      await fetchAllTrainLocations();

      // Emit event to notify parent that data has loaded
      emit('data-loaded');

      locationsLoading.value = false;
    } else {
      throw new Error('Data content is not an array');
    }
  } catch (error) {
    console.error('There was a problem with the axios operation:', error);
    locationsLoading.value = false;
  }
};

onMounted(async () => {
  await fetchTrains();
  fetchTrainsInterval = setInterval(fetchTrains, 60000);

  // Set up socket listeners for train updates
  if (socketStore.socket) {
    socketStore.socket.on('trackProtectionStarted', () => {
      fetchTrains();
    });

    socketStore.socket.on('trackProtectionEnded', () => {
      fetchTrains();
    });

    socketStore.socket.on('lineupTrackProtectionUpdated', () => {
      fetchTrains();
    });
  }
});

onUnmounted(() => {
  clearInterval(fetchTrainsInterval);

  if (socketStore.socket) {
    socketStore.socket.off('lineupTrackProtectionUpdated');
    socketStore.socket.off('trackProtectionStarted');
    socketStore.socket.off('trackProtectionEnded');
  }
});
</script>

<template>
  <div>
    <!-- Compact Table View without borders -->
    <DataTable class="p-0" :value="displayedTrains" data-key="train_row_id" 
               responsiveLayout="scroll" stripedRows size="small"
               :paginator="false">
      <template #empty>
        No Trains On Lineup.
      </template>
      <Column field="dashed_train_id" header="Train ID" sortable style="width: 100px">
        <template #body="slotProps">
          <router-link :to="`/trains/${slotProps.data.train_row_id}`" class="text-blue-600 dark:text-blue-400 hover:underline text-sm font-bold">
            {{ slotProps.data.dashed_train_id || `Train ${slotProps.data.train_row_id}` }}
          </router-link>
        </template>
      </Column>
      <Column field="location" header="Location">
        <template #body="slotProps">
          <span class="text-[14px] font-semibold" v-tooltip.top="trainLocations[slotProps.data.train_row_id]?.station 
            ? `${trainLocations[slotProps.data.train_row_id].description || ''} ${trainLocations[slotProps.data.train_row_id].station} ${trainLocations[slotProps.data.train_row_id].provstate || ''} @ ${trainLocations[slotProps.data.train_row_id].time || ''}` 
            : 'Location Unavailable'">
            {{ trainLocations[slotProps.data.train_row_id]?.station 
              ? `${trainLocations[slotProps.data.train_row_id].station}${trainLocations[slotProps.data.train_row_id].time ? ' @ ' + trainLocations[slotProps.data.train_row_id].time : ''}` 
              : "Location Unavailable" }}
          </span>
        </template>
      </Column>
      <Column field="eta_string" header="ETA" sortable>
        <template #body="slotProps">
          <div class="flex flex-col gap-1">
            <template v-if="Array.isArray(slotProps.data.etaHistory) && slotProps.data.etaHistory.length">
              <div
                v-for="(etaEntry, index) in slotProps.data.etaHistory"
                :key="`eta-${slotProps.data.train_row_id}-${index}`"
                class="flex items-center gap-1"
                v-tooltip.top="formatEtaTooltip(etaEntry)"
              >
                <i :class="`${etaSourceMeta(etaEntry.dataSource).icon} ${etaSourceMeta(etaEntry.dataSource).iconClass} text-[14px]`"></i>
                <span :class="`text-[14px] font-semibold ${etaSourceMeta(etaEntry.dataSource).textClass}`">
                  {{ formatEtaDisplay(etaEntry.eta) }}
                  <span class="text-[10px] ml-1">({{ etaSourceMeta(etaEntry.dataSource).label }})</span>
                </span>
              </div>
            </template>

            <div v-else class="flex items-center">
              <i class="pi pi-ban text-red-500 dark:text-red-400 mr-0.5 text-[14px]"></i>
              <span class="text-[14px] font-semibold text-red-600 dark:text-red-300">
                No ETA
              </span>
            </div>

            <!-- RMS Calculated ETA interval -->
            <div v-if="trainLocations[slotProps.data.train_row_id]?.etaIntervalString"
                 class="flex items-center"
                 v-tooltip.top="trainLocations[slotProps.data.train_row_id].etaIntervalString + ' (RMS)'">
              <i class="pi pi-hourglass text-amber-500 dark:text-amber-400 mr-0.5 text-[14px]"></i>
              <span class="text-[14px] font-semibold text-amber-600 dark:text-amber-300">
                {{ trainLocations[slotProps.data.train_row_id].etaIntervalString.replace('in ', '') }}
                <span class="text-amber-500 dark:text-amber-400 text-[10px]">(RMS)</span>
              </span>
            </div>

            <!-- RMS Calculated ETA time -->
            <div v-if="trainLocations[slotProps.data.train_row_id]?.etaTime"
                 class="flex items-center"
                 v-tooltip.top="new Date(trainLocations[slotProps.data.train_row_id].etaTime).toLocaleString('en-US', {
                   hour: '2-digit',
                   minute: '2-digit',
                   year: 'numeric',
                   month: 'long',
                   day: 'numeric',
                   hourCycle: 'h23'
                 }) + ' (RMS)'">
              <i class="pi pi-calendar text-emerald-500 dark:text-emerald-400 mr-0.5 text-[14px]"></i>
              <span class="text-[14px] font-semibold text-emerald-600 dark:text-emerald-300">
                {{ new Date(trainLocations[slotProps.data.train_row_id].etaTime).toLocaleString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  month: 'short',
                  day: 'numeric',
                  hourCycle: 'h23'
                }) }}
                <span class="text-emerald-500 dark:text-emerald-400 text-[10px]">(RMS)</span>
              </span>
            </div>
          </div>
        </template>
      </Column>
      <Column header="Units">
        <template #body="slotProps">
          <div class="flex flex-wrap gap-x-1 gap-y-0.5">
              <span v-if="slotProps.data.gensetCount > 0" class="bg-green-100 text-green-800 text-[10px] font-bold px-1.5 py-0 rounded">
              G:{{ slotProps.data.gensetCount }}
            </span>
            <span v-if="slotProps.data.electricCount > 0" class="bg-blue-100 text-blue-800 text-[10px] font-bold px-1.5 py-0 rounded">
              E:{{ slotProps.data.electricCount }}
            </span>
            <span v-if="slotProps.data.requestCount > 0" class="bg-purple-100 text-purple-800 text-[10px] font-bold px-1.5 py-0 rounded">
              R:{{ slotProps.data.requestCount }}
            </span>
            <span v-if="slotProps.data.dieselCount > 0" class="bg-amber-100 text-amber-800 text-[10px] font-bold px-1.5 py-0 rounded">
              D:{{ slotProps.data.dieselCount }}
            </span>
          </div>
        </template>
      </Column>
    </DataTable>

    <!-- Show "Showing X of Y trains" message if maxTrains is provided -->
    <div v-if="props.maxTrains && trains.length > props.maxTrains" class="text-center text-xs text-gray-500 mt-1">
      Showing {{ props.maxTrains }} of {{ trains.length }} trains
    </div>
  </div>
</template>

<style scoped>
/* Ensure the table fits properly in the dashboard */
:deep(.p-datatable) {
  font-size: 0.75rem;
}

:deep(.p-datatable-wrapper) {
  max-height: 250px;
  overflow-y: auto;
}
</style>
